import React,{useState,useEffect} from 'react'
import {useSelector,useDispatch} from 'react-redux'
import Button from 'react-bootstrap/Button'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Form from 'react-bootstrap/Form'
import {
	DragDropContext,
	Droppable,
	Draggable,
} from 'react-beautiful-dnd'
import {
	prettyRound,
	ellipsis,
	capitalize
} from '../../../util/util'
import styles from './Foods.module.css'
import {useEvent} from '../../../hooks/events'
import useWindowSize from '../../../hooks/useWindowSize'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import {
	updateNutrtionSimplifiedFood,
	makeNutritionSimplified,
	removeNutritionHeadings,
	createNutritionHeading,
	makeNutritionStandard,
	editNutritionHeading,
	removeNutritionItems,
	removeFoodFromMeal,
	updateRoutineOrder,
	groupFoodsIntoMeal,
	updateFoodDetails,
	updateFoodOrder,
	updateNutritionOrder,
	updateMealOrder,
	ungroupMeal,
	removeMeal,
	deleteItem,
	editMeal,
	updateEvent
} from '../../../state/events'
import {
	update as updateResourceLibrary
} from '../../ResourceLibrary/slice'
import {useDebounceCallback} from '@react-hook/debounce'
import useS3DirectUpload from '../../../hooks/useS3DirectUpload'
import FoodPortionSelect from '../../FoodPortionSelect/FoodPortionSelect'
import {
	mealToMacroMap,
	foodToMacroMap,
	nutritionItemstoMacroMap
} from '../../../util/nutrition'

// Simplified logger components for meals and foods
const LoggerFood = ({food,idx})=>{
	const macroMap = foodToMacroMap(food)
	const mainMacros = ['calories', 'protein', 'carbs', 'fat']
	
	return (
		<div className={styles.FoodRow} style={{padding: '12px'}}>
			<div className="d-flex align-items-center w-100">
				{/* Image placeholder or actual image if available */}
				<div 
					style={{
						width: '80px',
						height: '80px',
						flexShrink: 0,
						borderRadius: '8px',
						backgroundColor: '#f0f0f0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						marginRight: '12px',
						overflow: 'hidden'
					}}
				>
					{food.image ? (
						<img 
							src={food.image} 
							alt={food.name}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover'
							}}
						/>
					) : (
						<i className="fas fa-utensils" style={{color: '#ccc', fontSize: '24px'}} />
					)}
				</div>
				
				{/* Food name and macros */}
				<div style={{flex: '1 1 auto'}}>
					<div className="mb-2">
						<strong>{food.name}</strong>
					</div>
					<div className="d-flex flex-wrap">
						{mainMacros.map(macro => {
							const macroData = macroMap[macro]
							if (!macroData) return null
							return (
								<small key={macro} className="text-muted mr-3">
									<strong className="text-dark">{capitalize(macro)}:</strong> {macroData.qty.toFixed(0)}{macroData.unit}
								</small>
							)
						})}
					</div>
				</div>
			</div>
		</div>
	)
}

const LoggerMeal = ({meal,idx})=>{
	const macroMap = mealToMacroMap(meal)
	const mainMacros = ['calories', 'protein', 'carbs', 'fat']
	
	return (
		<div className={styles.FoodRow} style={{padding: '12px'}}>
			<div className="d-flex align-items-center w-100">
				{/* Meal image */}
				<div 
					style={{
						width: '80px',
						height: '80px',
						flexShrink: 0,
						borderRadius: '8px',
						backgroundColor: '#f0f0f0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						marginRight: '12px',
						overflow: 'hidden'
					}}
				>
					{meal.image ? (
						<img 
							src={meal.image} 
							alt={meal.name}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover'
							}}
						/>
					) : (
						<i className="fas fa-list-ol" style={{color: '#ccc', fontSize: '24px'}} />
					)}
				</div>
				
				{/* Meal name and macros */}
				<div style={{flex: '1 1 auto'}}>
					<div className="mb-2">
						<strong>{meal.name}</strong>
						{meal.time && <span className="text-muted ml-2">({meal.time})</span>}
					</div>
					<div className="d-flex flex-wrap">
						{mainMacros.map(macro => {
							const macroData = macroMap[macro]
							if (!macroData) return null
							return (
								<small key={macro} className="text-muted mr-3">
									<strong className="text-dark">{capitalize(macro)}:</strong> {macroData.qty.toFixed(0)}{macroData.unit}
								</small>
							)
						})}
					</div>
				</div>
			</div>
		</div>
	)
}

const MacroTotals = ({macroTotalMap,macroGoalsMap})=>{
	const getMacroSortValue = (macro)=>{
		switch(macro){
			case "calories": return 0
			case "protein": return 1
			case "carbs": return 2
			case "fat": return 3
			case "sodium": return 4
			case "fiber": return 5
			default: return 6
		}
	}

	return (
		<div className="text-center mt-2">
			<strong className="mr-2 d-block">Macro Totals:</strong>

			{Object.entries(macroTotalMap)
				.sort(([macroA],[macroB])=>{
					return getMacroSortValue(macroA) - getMacroSortValue(macroB)
				})
				.map(([macro,{qty,unit}])=>(
					<span className="mx-2">
						{macro}: <span className="text-dark">{qty.toFixed(0)}{(Boolean(macroGoalsMap)&&Boolean(macroGoalsMap[macro]))&&` / ${macroGoalsMap[macro]}`} {unit}</span>
					</span>
				))
			}
		</div>
	)
}

const SimplifiedFood = ({macro,food,nutrition_id})=>{
	const dispatch = useDispatch()
	const dispatchUpdate = (quantity,id) => {
		dispatch(updateNutrtionSimplifiedFood({nutrition_id,quantity,id}))
	}
	const debouncedDispatchUpdate = useDebounceCallback(dispatchUpdate,300)
	const [value,setValue] = useState(food.quantity)
	const handleChange = (inputValue,i)=>{
		const maxValue = 20
		const clippedValue = Math.max(Math.min(inputValue,maxValue),0)
		const newValue = macro === 'water' ? clippedValue/8 : clippedValue
		debouncedDispatchUpdate(clippedValue,food.id)
		setValue(clippedValue)
	}
	return (
		<div>
			<Form.Label>{macro}{macro==='water'?' (servings - 8 ounces each)':' (servings)'}</Form.Label>
			<Form.Control 
				onChange={
					e => { 
						handleChange(e.target.value.replace(/\D/g,'')) 
					}
				}
				placeholder={macro}
				value={value}
			/>
			{macro==='water'&&
				<div>
					<small className="text-muted">Enter 8 for 64 ounces in a day</small>
				</div>
			}
		</div>
	)
}

const SimplifiedFoods = ({nutrition_id,foods})=>{
	//list of macros that must be assigned for simplifed/MAX nutrtion
	const macros = ['protein','carbohydrates','healthy fat','fruit','vegetables','water']
	return (
		<div>
			{foods.length === 0
				? <Spinner animation="border" size="sm" />
				: macros.map((macro,i)=>(
					<SimplifiedFood macro={macro} food={foods[i]} nutrition_id={nutrition_id}/>
				))
			}
		</div>
	)
}

const Assignable = ({assignable,food})=>{
	const dispatch = useDispatch()

	const handleInput = (userInput)=>{
		dispatch(updateFoodDetails({
			nutrition_id:food.nutrition_id,
			[assignable]:userInput,
			food_id:food.food_id,
		}))
	}

	const displayText = {
		quantity:'qty',
		meal_time:'meal time'
	}[assignable]

	const placeholderText = {
		quantity:'amount',
		meal_time:'meal time'
	}[assignable]

	const hasValue = Boolean(food[assignable])
	const debouncedHandleInput = useDebounceCallback(handleInput,300)
	return (
		<div className="px-1" style={{width:'33%',flex:'1 1 33%'}}>
			<OverlayTrigger placement="right" overlay={
				<Tooltip>{displayText}</Tooltip>
			}>
				<div style={{position:'relative'}}>
					<input
						defaultValue={food[assignable]}
						onChange={ e => { debouncedHandleInput(e.target.value) } }
						className={styles.SimpleInput} 
						placeholder={placeholderText} 
						size="sm"
					/>

					{hasValue&&
						<small style={{
							transform:'translate(-50%,-40%)',
							position:'absolute',
							fontSize:'9px',
							color:'#777',
							left:'50%',
							top:0,
						}}>
							{displayText}
						</small>
					}
				</div>
			</OverlayTrigger>
		</div>
	)
}

const InsertButton = ({insertPosition=1,nutrition_id,meal_id=0,tgtResourceType})=>{

	const {
		insertPosition:activePosition,
		show:showLibrary,
		tgtResourceType:activeResourceType,
		tgtResourceId:activeResourceId,
		replace
	} = useSelector( state => state.resourceLibrary )

	const replacing = Boolean(replace.id)

	const isActive = (
		insertPosition === activePosition 
		&& 
		showLibrary
		&&
		!replacing
		&&
		//depending on if we are adding foods to a meal or directly
		//to the nutrition plan we need to correclty highlight the 
		//insert button. these checks ensure the the correct button
		//is highlighted
		activeResourceType === tgtResourceType
		&&
		(
			activeResourceId === meal_id 
			|| 
			activeResourceId === nutrition_id
		)
	)

	const dispatch = useDispatch()

	const handleInsertHeading = ()=>{
		dispatch(createNutritionHeading({
			routine_order:insertPosition,
			nutrition_id
		}))
		dispatch(updateResourceLibrary({
			insertPosition:insertPosition+1
		}))
	}

	const handleInsertMeal = ()=>{
		dispatch(updateResourceLibrary({
			show:true,
			insertPosition,
			tgtResourceId:nutrition_id,
			tgtResourceType,
			resourceType:'recipe_catalog',
			replace:{
				id:false,
				type:false
			}
		}))
	}

	const handleInsertFood = ()=>{
		dispatch(updateResourceLibrary({
			show:true,
			insertPosition,
			tgtResourceId:Boolean(meal_id) ? meal_id : nutrition_id,
			tgtResourceType,
			resourceType:'food',
			replace:{
				id:false,
				type:false
			}
		}))
	}

	return (
		<div className={styles.InsertFoodButtonContainer}>
			<div className={[styles.InsertFoodButton,isActive?styles.Active:''].join(' ')}>
				{isActive?'>':'+'}
				<div className={styles.InsertMenu}>

					<ButtonGroup size="sm">
						{tgtResourceType==='meal'&&
							<OverlayTrigger overlay={
								<Tooltip>
									Add food here
								</Tooltip>
							}>
								<Button 
									onClick={handleInsertFood}
									variant="outline-success"
								>
									<i className="fas fa-utensils"></i> Food
								</Button>
							</OverlayTrigger>
						}
						{tgtResourceType==='nutrition'&&
							<>
								<OverlayTrigger overlay={
									<Tooltip>
										Add recipe/meal here
									</Tooltip>
								}>
									<Button 
										onClick={handleInsertMeal}
										variant="outline-info"
									>
										<i className="fas fa-list-ol"></i> Recipe
									</Button>
								</OverlayTrigger>
								<OverlayTrigger overlay={
									<Tooltip>
										Add heading here
									</Tooltip>
								}>
									<Button 
										onClick={handleInsertHeading}
										variant="outline-primary"
									>
										<i className="fas fa-heading"></i> Heading
									</Button>
								</OverlayTrigger>
							</>
						}
					</ButtonGroup>
					<i className="fas fa-long-arrow-alt-right"></i>
				</div>
			</div>
		</div>
	)
}

const Food = ({food,idx})=>{
	const {selectedItems} = useSelector(state=>state.resourceLibrary)
	const dispatch = useDispatch()
	const assignables = ['quantity','meal_time']
	
	const handleNotesInput = userInput => {
		dispatch(
			updateFoodDetails({
				nutrition_id:food.nutrition_id,
				food_id:food.food_id,
				notes:userInput,
			})
		)
	}

	const handlePortionSelect = portion => {
		dispatch(
			updateFoodDetails({
				nutrition_id:food.nutrition_id,
				food_id:food.food_id,
				food_db_portion_id:portion.id,
			})
		)
	}

	const handleRemove = ()=>{
		if(!window.confirm('Are you sure?')) return
		dispatch(deleteItem({
			event_id:food.nutrition_id,
			event_type:'nutrition',
			item_id:food.food_id,
		}))
	}

	//get replace state. This holds the id and type of the exercise
	//getting replaced. 
	const replace = useSelector(
		state => state.resourceLibrary.replace
	)
	const replacing = replace.id === food.food_id
	
	const handleReplace = ()=>{
		//if current food is the one getting replaced
		//then toggle the replace off.
		//else enable replace for clicked food
		if(replacing){
			dispatch(updateResourceLibrary({
				replace:{}
			}))
		}else{
			dispatch(updateResourceLibrary({
				show:true,
				replace:{
					id:food.food_id,
				},
				resourceType:'food',
				tgtResourceType:'nutrition',
				tgtResourceId:food.nutrition_id,
			}))
		}
	}

	const isSelected = ()=>{
		for (var i = selectedItems.length - 1; i >= 0; i--) {
			const item = selectedItems[i]
			if(item.type==='food'&&item.id===food.food_id){
				return true
			}
		}
		return false
	}

	const handleCheck = ()=>{
		if(isSelected()){
			dispatch(updateResourceLibrary({
				selectedItems:selectedItems.filter(
					item=>item.type!=='food'||item.id!==food.food_id
				)
			}))
		}else{
			dispatch(updateResourceLibrary({
				selectedItems:[
					...selectedItems,
					{type:'food',id:food.food_id}
				]
			}))
		}
	}

	//state for UI interaction on notes
	const [notesOpen,setNotesOpen] = useState(false)

	const debouncedHandleNotesInput = useDebounceCallback(handleNotesInput,300)
	const qty = parseFloat(food.quantity) || 0
	//destructure macros per unit
	const {calories,protein,fat,carbs} = food
	return (
		<Draggable
			key={`${food.food_id}`}
			draggableId={`${food.food_id}`}
			index={idx}
		>
			{(provided, snapshot) => (
				<div 
					ref={provided.innerRef}
					{...provided.draggableProps}
				>
					<div
						className={styles.FoodRow}
					>
						<div
							id={`group-check-tab-${food.food_id}-food`}
							style={{
								position:'absolute',
								top:'50%',
								left:0,
								transform:'translate(-100%,-50%)',
								background:'#fff',
								borderColor:'#ccc',
								borderWidth:'1px 0 1px 1px',
								borderStyle:'solid',
								width:'32px',
								padding:'4px 8px',
								borderRadius:'8px 0 0 8px'
							}}
						>
							<Form.Check
								checked={isSelected()}
								onChange={handleCheck}
							/>
						</div>
						<div 
							className={styles.FoodHandle}
							{...provided.dragHandleProps}
						>
							<i className="fas fa-grip-vertical"/>
						</div>
						<div className={styles.Food}>
							<div className={styles.FoodNameContainer}>
								<span className={styles.FoodName}>
									<OverlayTrigger placement="top" overlay={
										<Tooltip>
											{food.name}
											<br/>
											<small>{replacing?'Cancel replace':'Click to replace'}</small>
										</Tooltip>
									}>
										<span 
											className={replacing&&'text-primary'}
											onClick={handleReplace}
											tabindex={0}
										>
											{replacing&&
												<i className="fas fa-exchange-alt mr-1" />
											}
											{food.name}
										</span>
									</OverlayTrigger>
								</span>
								{/*food.portion_unit!=='not specified'&&`srv: ${prettyRound(qty*food.portion_size,1)}${food.portion_unit} `*/}
								<FoodPortionSelect
									onChange={handlePortionSelect}
									initialPortionId={food.food_db_portion_id}
									portions={food.portions}
									qty={qty}
								/>
							</div>
							<div className={styles.Assignables}>
								{assignables.map(assignable=>(
									<Assignable assignable={assignable} food={food}/>
								))}
							</div>
							<div className={styles.NotesOptions}>
								<div className={styles.NotesContainer}>
									<OverlayTrigger placement="right" overlay={
										<Tooltip>notes</Tooltip>
									}>
										<Form.Control
											/*
												These multiple handlers are for UX
												make sure this is not edited UNLESS
												you UNDERSTAND the logic
											*/
											onBlur={()=>{setNotesOpen(false)}} 
											onMouseLeave={()=>{setNotesOpen(false)}} 
											onClick={()=>{setNotesOpen(true)}}
											onFocus={()=>{setNotesOpen(true)}}
											onChange={ e => {
												debouncedHandleNotesInput(e.target.value)
											}}
											style={{
												height:'31px',
												whiteSpace:'pre-line'
											}}
											className={[
												styles.Notes,
												notesOpen?styles.Open:''
											].join(' ')}
											defaultValue={food.notes}
											as="textarea" 
											placeholder="notes" 
											size="sm"
										/>
									</OverlayTrigger>
								</div>
								<div className="d-flex align-items-center">
									<Button onClick={handleRemove} variant="link" className="text-danger">
										<i className="fas fa-times"/>
									</Button>
								</div>
							</div>
						</div>
					</div>
					<InsertButton 
						insertPosition={food.routine_order+1} 
						nutrition_id={food.nutrition_id}
						tgtResourceType="nutrition"
					/>
				</div>
			)}
		</Draggable>
	)
}

const MealFood = ({food,idx,meal_id,nutrition_id})=>{
	const dispatch = useDispatch()
	const [localQuantity,setLocalQuantity] = useState(food.quantity)

	const {
		replace:{
			id:replaceId,
			type:replaceType
		},
		show:libraryIsOpen,
	} = useSelector(state=>state.resourceLibrary)

	const isReplacing = (
		parseInt(replaceId) === parseInt(food.food_id) 
		&& 
		replaceType === 'food'
		&&
		libraryIsOpen
	)

	const handleEdit = (update)=>{
		dispatch(updateFoodDetails({
			nutrition_id:food.nutrition_id,
			food_id:food.food_id,
			...update
		}))
	}

	const handleRemove = ()=>{
		if (!window.confirm('Are you sure you want to remove this food?')) return
		dispatch(removeFoodFromMeal({
			food_id:food.food_id,
			meal_id,
			nutrition_id
		}))
	}

	return (
		<Draggable
			key={`${food.food_id}`}
			draggableId={`${food.food_id}`}
			index={idx}
		>
			{(provided, snapshot) => (
				<div 
					ref={provided.innerRef}
					{...provided.draggableProps}
					style={{
						...provided.draggableProps.style,
						paddingBottom:'6px'
					}}
				>
					<div className="d-flex align-items-center w-100">
						<div 
							className={[styles.FoodHandle,'mr-1'].join(' ')}
							{...provided.dragHandleProps}
						>
							<i className="fas fa-grip-vertical"/>
						</div>
						<div 
							className="d-flex align-items-start bg-white flex-wrap flex-md-nowrap"
							style={{
								paddingBottom:'6px',
								borderBottom:'1px solid #ccc',
								flex:'1 1 auto',
							}}
						>
							<div className="w-100">
								<small 
									onClick={()=>{
										console.log(food)
										dispatch(
											updateResourceLibrary({
												show:true,
												resourceType:'food',
												tgtResourceType:'meal_food',
												tgtResourceId:meal_id,
												replace:{
													id:food.food_id,
													type:'food'
												},
												insertPosition:food.routine_order
											})
										)
									}}
									style={{
										width:'250px',
										flex:'0 0 auto',
										cursor:'pointer'
									}}
								>
									{isReplacing&&<i className="fas fa-exchange-alt text-primary mr-1"/>}
									{ellipsis(food.food_name,40)}
								</small>
								{/*food.portion_unit!=='not specified'?`srv: ${food.portion_size} ${food.portion_unit} |`:''*/} 
								<FoodPortionSelect
									onChange={portion=>{
										handleEdit({food_db_portion_id:portion.id})
									}}
									initialPortionId={food.food_db_portion_id}
									portions={food.portions}
									qty={food.quantity}
								/>
							</div>
							<div className="d-flex ml-md-auto flex-md-column justify-content-between w-100">
								<div className="d-flex align-items-center justify-content-end flex-column flex-md-row">
									<small className="mr-2">Quantity:</small>
									<Form.Control 
										style={{width:'80px'}}
										onChange={e=>{
											handleEdit({quantity:e.target.value})
										}}
										defaultValue={prettyRound(food.quantity,2)}
										size="sm" 
									/>
								</div>
								<div className="text-right d-flex align-items-center justify-content-end mt-1 flex-column flex-md-row">
									<Form.Check 
										onChange={e=>{
											handleEdit({is_optional:Number(e.target.checked)})
										}}
										checked={Boolean(food.is_optional)}
										label="optional"
										id={`optional-meal-food-${food.food_id}`}
									/>
									<Button 
										onClick={handleRemove}
										variant="outline-danger"
										className="ml-2"
										size="sm"
									>
										Remove
									</Button>
								</div>
							</div>
						</div>
					</div>
					<InsertButton 
						insertPosition={food.routine_order+1}
						nutrition_id={food.nutrition_id}
						meal_id={meal_id}
						tgtResourceType="meal"
					/>
				</div>
			)}
		</Draggable>
	)
}

const Meal = ({meal,idx})=>{
	const {selectedItems} = useSelector(state=>state.resourceLibrary)
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()

	const {processUpload} = useS3DirectUpload()

	const macroTotalMap = mealToMacroMap(meal)

	const [showImageEditor,setShowImageEditor] = useState(false)

	const isSelected = ()=>{
		for (var i = selectedItems.length - 1; i >= 0; i--) {
			const item = selectedItems[i]
			if (item.type==='meal'&&item.id===meal.id) {
				return true
			}
		}
		return false
	}

	const handleCheck = ()=>{
		if (isSelected()) {
			dispatch(updateResourceLibrary({
				selectedItems:selectedItems.filter(
					item=>item.type!=='meal'||item.id!==meal.id
				)
			}))
		} else {
			dispatch(updateResourceLibrary({
				selectedItems:[
					...selectedItems,
					{type:'meal',id:meal.id}
				]
			}))
		}
	}

	const [localName,setLocalName] = useState(meal.name)
	const [localTime,setLocalTime] = useState(meal.time)
	const [localDescription,setLocalDescription] = useState(meal.description)
	const [localDirections,setLocalDirections] = useState(meal.directions)
	const [localIngredients,setLocalIngredients] = useState(meal.ingredients)

	useEffect(()=>{
		setLocalName(meal.name)
		setLocalTime(meal.time)
		setLocalDescription(meal.description)
		setLocalDirections(meal.directions)
		setLocalIngredients(meal.ingredients)
	},[meal])

	const applyLocalUpdate = (update)=>{
		for (const key in update) {
			const value = update[key]
			switch(key){
				case "name":
					setLocalName(value)
				break
				case "time":
					setLocalTime(value)
				break
				case "description":
					setLocalDescription(value)
				break
				case "directions":
					setLocalDirections(value)
				break
				case "ingredients":
					setLocalIngredients(value)
				break
				default: 
					throw new Error(`Local value update not handled for case: ${key}`)
				break
			}
		}
	}

	const dispatchEdit = (update)=>{
		return statusDispatch(editMeal({
			nutrition_id:meal.nutrition_id,
			id:meal.id,
			update
		}),'meal','edit')
	}

	const debouncedDispatchEdit = useDebounceCallback(
		dispatchEdit,
		500
	)

	//to prevent sending countless api calls on each key stroke 
	//but to also allow these fields to be controled we use an
	//immediate local state update and a debounced global/server
	const handleEdit = (update) => {
		applyLocalUpdate(update)
		debouncedDispatchEdit(update)
	}

	const handleRemove = ()=>{
		if(!window.confirm('Are you sure you want to remove this meal?')) return 
		dispatch(
			removeMeal(meal)
		)
	}

	const handleUngroup = ()=>{
		if(!window.confirm('Are you sure you want this meal?')) return 
		dispatch(ungroupMeal({
			nutrition_id:meal.nutrition_id,
			id:meal.id
		}))
	}

	const [savingImage,setSavingImage] = useState(false)
	const [newImage,setNewImage] = useState(null)
	const handleSaveImage = async ()=>{
		setSavingImage(true)
		const s3Res = await processUpload(newImage)
		dispatchEdit({image:s3Res.payload.url}).then(()=>{
			setNewImage(null)
			setSavingImage(false)
			setShowImageEditor(false)
		})
	}

	const handleRemoveImage = ()=>{
		if(!window.confirm('Are you sure you want to remove this meal image?')) return 
		dispatchEdit({image:''})
		setShowImageEditor(false)
	}

	const handleDragEnd = result => {
		const droppedOutsideOfList = !Boolean(result.destination)
		if (meal.foods.length <= 1||droppedOutsideOfList) return
		const {source:{index:srcIdx},destination:{index:tgtIdx}} = result
		//get a temp array with the src item removed
		const tmpNoSrc = [
			...meal.foods.slice(0,srcIdx),
			...meal.foods.slice(srcIdx+1)
		]
		//define the update with the src item at the target index
		//perform deep copy "hack" so we can mutate the update object
		//with the new foods orders
		const foodsUpdate = JSON.parse(JSON.stringify([
			...tmpNoSrc.slice(0,tgtIdx),
			meal.foods[srcIdx],
			...tmpNoSrc.slice(tgtIdx)
		]))
		//update the foods orders to reflect the new order
		foodsUpdate.forEach((food,i)=>{
			food.routine_order = i+1
		})

		//dispatch async api action to persist the new order
		dispatch(updateMealOrder({
			nutrition_id:meal.nutrition_id,
			meal_id:meal.id,
			//map the update to array of typed ids
			new_order:foodsUpdate.map(food=>food.food_id),
			//update is passed only so that we can set the new routine
			//in [updateRoutineOrder.pending] (ie optimistic UI update)
			foodsUpdate,
		}))
	}

	return (
		<Draggable
			key={`${meal.id}`}
			draggableId={`${meal.id}`}
			index={idx}
		>
			{(provided, snapshot) => (
				<div
					ref={provided.innerRef}
					{...provided.draggableProps}
				>
					<div className={styles.FoodRow}>
						<div
							id={`group-check-tab-${meal.id}-meal`}
							style={{
								position:'absolute',
								top:'50%',
								left:0,
								transform:'translate(-100%,-50%)',
								background:'#fff',
								borderColor:'#ccc',
								borderWidth:'1px 0 1px 1px',
								borderStyle:'solid',
								width:'32px',
								padding:'4px 8px',
								borderRadius:'8px 0 0 8px'
							}}
						>
							<Form.Check
								checked={isSelected()}
								onChange={handleCheck}
							/>
						</div>
						<div 
							className={styles.FoodHandle}
							{...provided.dragHandleProps}
						>
							<i className="fas fa-grip-vertical"/>
						</div>
						<div className="p-1 w-100">
							<div className="w-100 align-items-start d-flex flex-wrap">
								<OverlayTrigger overlay={
									<Tooltip>Add/Update Picture</Tooltip>
								}>
									{Boolean(meal.image)?
										<img 
											onClick={()=>{
												setShowImageEditor(true)
											}}
											className="rounded mr-1"
											src={meal.image}
											style={{
												objectFit:'cover',
												width:'31px',
												height:'31px',
												cursor:'pointer'
											}}
										/>
										:
										<button 
											onClick={()=>{
												setShowImageEditor(true)
											}}
											className="mr-1"
											style={{
												border:'1px solid #ccc',
												borderRadius:'7px',
												background:'none',
												color:'#777',
												width:'31px',
												height:'31px'
											}}>
											<i className="fa fa-image" />
										</button>
									}
								</OverlayTrigger>
								<Form.Control 
									onChange={e=>{
										handleEdit({name:e.target.value})
									}}
									style={{
										width:'200px'
									}}
									className="mr-1 mb-1 mb-md-0"
									value={localName} 
									placeholder="Meal Name"
									size="sm"
								/>
								<Form.Control 
									className="mr-1"
									style={{width:'120px',flex:'0 0 auto'}}
									onChange={ e => {
										handleEdit({time:e.target.value})
									} }
									value={localTime}
									placeholder="Meal Time"
									size="sm"
								/>
								<div className="ml-auto">
									<OverlayTrigger overlay={
										<Tooltip>Replace with a recipe</Tooltip>
									}>
										<Button 
											style={{flex:'0 0 auto'}}
											className="mr-1 ml-auto"
											onClick={()=>{
												dispatch(updateResourceLibrary({
													show:true,

													resourceType:'recipe',
													tgtResourceType:'meal',
													tgtResourceId:meal.id,
													replace:{
														type:'meal',
														id:meal.id
													}
												}))
											}}
											variant="outline-info"
											size="sm"
										>
											<span className="mr-1 d-none d-md-inline-block">Replace</span>
											<i className="fa fa-exchange-alt"/> 
										</Button>
									</OverlayTrigger>
									{/*<OverlayTrigger overlay={
										<Tooltip>Ungroup meal into foods</Tooltip>
									}>
										<Button 
											className="mr-1"
											onClick={handleUngroup}
											variant="outline-warning"
											size="sm"
										>
											<i className="fa fa-object-ungroup"/> 
										</Button>
									</OverlayTrigger>*/}
									<OverlayTrigger overlay={
										<Tooltip>Remove meal and all associated foods</Tooltip>
									}>
										<Button 
											onClick={handleRemove}
											variant="outline-danger"
											size="sm"
										>
											<i className="fa fa-trash"/> 
										</Button>
									</OverlayTrigger>
								</div>
							</div>
							<small className="d-block">
								<strong>Description</strong>
							</small>
							<Form.Control
								onChange={e=>{
									handleEdit({description:e.target.value})
								}}
								value={localDescription}
								placeholder="description"
								className="mt-1"
								as="textarea"
								size="sm"
							/>
							<small className="d-block">
								<strong>Directions</strong>
							</small>
							<Form.Control
								onChange={ e => {
									handleEdit({directions:e.target.value})
								}}
								value={localDirections}
								placeholder="directions"
								className="mt-1"
								as="textarea"
								size="sm"
							/>
							{localIngredients!==''&&
								<>
									<small className="d-block">
										<strong>
											Ingredients (optional - e.g. seasonings)
										</strong>
									</small>
									<Form.Control
										onChange={e=>{
											handleEdit({ingredients:e.target.value})
										}}
										value={localIngredients}
										placeholder="ingredients"
										className="mt-1"
										as="textarea"
										size="sm"
									/>
								</>
							}
							<small className="d-flex align-items-start justify-content-between my-2">
								<strong>Ingredients</strong>
							</small>
							{meal.foods.length > 0 ?
								<DragDropContext onDragEnd={handleDragEnd}>
									<Droppable droppableId={`meal-items-${meal.id}`} type="meal-food">
										{(provided, snapshot) => (
											<div 
												style={{borderTop:'1px solid #ccc',paddingTop:'12px',paddingLeft:'18px'}}
												{...provided.droppableProps}
												ref={provided.innerRef}
											>
												{meal.foods.map( (item,i) => (
													item.type==='placeholder'
													? <Placeholder placeholder={item} />
													: <MealFood
														nutrition_id={meal.nutrition_id}
														meal_id={meal.id}
														food={item}
														idx={i}
													/>
												))}
												{provided.placeholder}
											</div>
										)}
									</Droppable>
								</DragDropContext>
								:
								<Button 
									onClick={()=>{
										dispatch(
											updateResourceLibrary({
												insertPosition:1,
												resourceType:'food',
												tgtResourceType:'meal',
												tgtResourceId:meal.id,
												show:true
											})
										)
									}}
									variant="outline-success"
									block
								>
									Add foods to meal
								</Button>
							}
							<MacroTotals macroTotalMap={macroTotalMap}/>
						</div>
						{showImageEditor&&
							<div 
								className={styles.EditorContainer}
								onClick={()=>{
									setShowImageEditor(false)
								}}
							>
								<div onClick={ e => {e.stopPropagation()} }>
									<div className="d-flex align-items-start">
										<Form.File
											disabled={savingImage}
											className="mb-1 mr-1"
											onChange={ e => {
												setNewImage(e.target.files[0])
											} }
											id={`meal-image-input-${meal.id}`}
											label="Select Image"
											size="sm"
											custom
										/>
										<Button 
											onClick={()=>{
												setShowImageEditor(false)
											}}
											variant="outline-danger"
										>
											Close
										</Button>
									</div>
									{(Boolean(meal.image)&&!Boolean(newImage))&&
										<img
											className="mb-1"
											src={meal.image}
											style={{
												display:'inline-block',
												objectFit:'cover',
												width:'120px',
												height:'120px',
												borderRadius:'7px'
											}}
										/>
									}
									{Boolean(newImage)&&
										<img
											className="mb-1"
											src={URL.createObjectURL(newImage)}
											style={{
												display:'inline-block',
												objectFit:'cover',
												width:'120px',
												height:'120px',
												borderRadius:'7px'
											}}
										/>
									}
									<div>
										{(Boolean(meal.image)&&!Boolean(newImage))&&
											<Button
												disabled={savingImage}
												onClick={handleRemoveImage}
												variant="outline-danger"
												size="sm"
											>
												Remove Image
											</Button>
										}
										{Boolean(newImage)&&
											<>
												<Button
													disabled={savingImage}
													className="mr-1"
													onClick={()=>{
														setNewImage(null)
													}}
													variant="outline-danger"
													size="sm"
												>
													{Boolean(meal.image)?'Keep Current Image':'Cancel'}
												</Button>
												<Button
													className="mr-1"
													disabled={savingImage}
													onClick={handleSaveImage}
													variant="outline-success"
													size="sm"
												>
													Save Image
												</Button>
												{savingImage&&
													<Spinner animation="border" size="sm"/>
												}
											</>
										}
									</div>
								</div>
							</div>
						}
					</div>
					<InsertButton
						nutrition_id={meal.nutrition_id}
						insertPosition={meal.routine_order+1}
						tgtResourceType="nutrition"
					/>
				</div>
			)}
		</Draggable>
	)
}

const MealPhoto = ({photo,idx})=>{
	//current meal photo usecase doesn't require editing in the editor
	return null
}

const Heading = ({heading,idx})=>{
	const {selectedItems} = useSelector(state=>state.resourceLibrary)
	const dispatch = useDispatch()
	const [localValue,setLocalValue] = useState(heading.heading)
	const dispatchEdit = (newHeading)=>{
		dispatch(editNutritionHeading({
			id:heading.id,
			update:{heading:newHeading}
		}))
	}
	const debouncedDispatchEdit = useDebounceCallback(dispatchEdit,500)
	const handleEdit = e => {
		setLocalValue(e.target.value)
		debouncedDispatchEdit(e.target.value)
	}
	const handleRemove = ()=>{
		if(!window.confirm('Are you sure you want to remove this heading?')) return
		dispatch(removeNutritionHeadings({
			nutrition_id:heading.nutrition_id,
			ids:[heading.id]
		}))
	}

	const isSelected = ()=>{
		for (var i = selectedItems.length - 1; i >= 0; i--) {
			const item = selectedItems[i]
			if(item.type==='heading'&&item.id===heading.id){
				return true
			}
		}
		return false
	}

	const handleCheck = ()=>{
		if(isSelected()){
			dispatch(updateResourceLibrary({
				selectedItems:selectedItems.filter(
					item=>item.type!=='heading'||item.id!==heading.id
				)
			}))
		}else{
			dispatch(updateResourceLibrary({
				selectedItems:[
					...selectedItems,
					{type:'heading',id:heading.id}
				]
			}))
		}
	}

	return (
		<Draggable
			key={`${heading.id}`}
			draggableId={`${heading.id}`}
			index={idx}
		>
			{(provided, snapshot) => (
				<div
					ref={provided.innerRef}
					{...provided.draggableProps}
				>
					<div
						className={styles.FoodRow}
					>
						<div
							id={`group-check-tab-${heading.id}-heading`}
							style={{
								position:'absolute',
								top:'50%',
								left:0,
								transform:'translate(-100%,-50%)',
								background:'#fff',
								borderColor:'#ccc',
								borderWidth:'1px 0 1px 1px',
								borderStyle:'solid',
								width:'32px',
								padding:'4px 8px',
								borderRadius:'8px 0 0 8px'
							}}
						>
							<Form.Check
								checked={isSelected()}
								onChange={handleCheck}
							/>
						</div>
						<div 
							className={styles.FoodHandle}
							{...provided.dragHandleProps}
						>
							<i className="fas fa-grip-vertical"/>
						</div>
						<div className="w-100 d-flex align-items-center">
							<Form.Control
								onChange={handleEdit} 
								placeholder="Heading"
								style={{
									padding:'8px',
									minHeight:'40px',
									height:'40px',
									fontSize:'18px',
									border:'none',
									textAlign:'center'
								}}
								as="textarea" 
								value={localValue}
							/>
							<div className="d-flex align-items-center">
								<Button onClick={handleRemove} variant="link" className="text-danger">
									<i className="fas fa-times"/>
								</Button>
							</div>
						</div>
					</div>
					<InsertButton
						nutrition_id={heading.nutrition_id}
						insertPosition={heading.routine_order+1}
						tgtResourceType="nutrition"
					/>
				</div>
			)}
		</Draggable>
	)
}

const Placeholder = ({placeholder})=>{
	
	return (
		<div>
			<div
				className={styles.FoodRow}
			>
				<div className="p-1 w-100 d-flex align-items-center justify-content-center flex-wrap">
					<Spinner className="mr-2" animation="border" size="sm"/> {placeholder.text}
				</div>
			</div>
		</div>
	)
}

const GroupMenu = ()=>{
	const params = new URLSearchParams(window.location.search)
	const nutrition_id = params.get('event_id')
	const {selectedItems:items} = useSelector(state=>state.resourceLibrary)
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()
	const [width,height] = useWindowSize()
	const [groupMenuHeight,setGroupMenuHeight] = useState(0)
	const [groupMenuTop,setGroupMenuTop] = useState(0)
	const groupMenuLeft = width < 767 ? 32 : -2
	const tickOffset = width < 767 ? 8 : -2
	const [status,setStatus] = useState('idle')

	const updateGroupMenu = ()=>{
		let minTop = Infinity
		let maxTop = -Infinity
		items.forEach( item => {
			const tabId = `group-check-tab-${item.id}-${item.type}`
			const tab = document.getElementById(tabId)
			if(!tab) return
			const boundingRect = tab.getBoundingClientRect()
			const scrollOffset = document.getElementById('Foods').getBoundingClientRect().top
			const h = boundingRect.height
			const w = boundingRect.width
			const topDelta = boundingRect.top - scrollOffset
			minTop = Math.min(minTop,topDelta+h/2)
			maxTop = Math.max(maxTop,topDelta+h/2)
		})
		setGroupMenuHeight( maxTop - minTop )
		setGroupMenuTop(minTop)
	}

	const handleClear = ()=>{
		dispatch(updateResourceLibrary({
			selectedItems:[]
		}))
	}

	const handleDelete = async ()=>{
		if (!window.confirm('Are you sure you want to remove these nutrition items?')) return
		dispatch(removeNutritionItems({nutrition_id,items}))
		dispatch(updateResourceLibrary({
			selectedItems:[]
		}))
	}

	const handleGroup = ()=>{
		const ids = items.map(i=>i.id)
		setStatus('pending')
		statusDispatch(groupFoodsIntoMeal({
			nutrition_id,
			ids
		}),'foods','group').then(()=>{
			setStatus('idle')
			handleClear()
		})
	}

	const allowGroup = !Boolean(items.find(item=>item.type!=='food'))

	useEffect(updateGroupMenu,[items,width,height])

	//available menu options
	const options = [
		{
			tip:'Uncheck all selected foods/headings',
			iconClass:"far fa-window-close text-danger",
			handler:handleClear,
			disabled:false
		},{
			tip:(
				allowGroup
				? 'Group Into Meal'
				: 'Only foods can be grouped into a meal. Please de-select any headings or meals.'
			),
			iconClass:"fa fa-layer-group text-primary",
			handler:handleGroup,
			disabled:!allowGroup
		},{
			tip:'Remove selected foods/headings',
			iconClass:"fa fa-trash-alt text-danger",
			handler:handleDelete,
			disabled:false
		}
	]

	//only render then menu if more than one item is selected
	if(items.length < 1) return null

	return (
		<>
			{items.map( item => {
				const tabId = `group-check-tab-${item.id}-${item.type}`
				const tab = document.getElementById(tabId)
				if( !tab ) return
				const boundingRect = tab.getBoundingClientRect()
				const scrollOffset = document.getElementById('Foods').getBoundingClientRect().top
				const h = boundingRect.height
				const w = boundingRect.width
				const topDelta = boundingRect.top - scrollOffset + h/2
				return (
					<div style={{
						height:'2px',
						background:`linear-gradient(to ${width<767?'left':'right'},#ccc,transparent)`,
						width:'26px',
						top:`${topDelta}px`,
						left:`${tickOffset}px`,
						position:'absolute'
					}}/>
				)
			})}
			<div style={{
				background:'#ccc',
				height:`${groupMenuHeight}px`,
				width:'2px',
				position:'absolute',
				top:`${groupMenuTop}px`,
				left:`${groupMenuLeft}px`,
				fontSize:'14px',
				transition:'0.2s',
				display:'flex',
				justifyContent:'center',
				alignItems:'center',
				flexFlow:'column',
				zIndex:10
			}}>

				<div style={{
					background:'#fff',
					border:'1px solid #ccc',
					borderRadius:'40px',
					padding:'8px 4px',
					width:'32px',
					textAlign:'center',
					boxShadow:'0 0 16px rgba(0,0,0,0.2)'
				}}>
					{status==='pending'&&
						<Spinner animation="border" size="sm" variant="primary"/>
					}
					{status!=='pending'&&options.map((opt,i)=>(
						<>
							<OverlayTrigger overlay={
								<Tooltip>
									{opt.tip}
								</Tooltip>
							}>
								<button 
									disabled={status==='pending'}
									tabIndex={0}
									onClick={()=>{
										if (opt.disabled) return
										opt.handler()
									}}
									style={{
										border:'none',
										background:'none',
										display:'block',
										width:'100%',
										padding:'0'
									}}
								>
									<i className={opt.iconClass} />
								</button>
							</OverlayTrigger>
							{(i<options.length-1) &&
								<hr className="my-2"/>
							}
						</>
					))}
				</div>
			</div>
		</>
	)
}

const Foods = ({nutrition_id}) => {
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()
	const nutrition = useEvent('nutrition',nutrition_id)
	const foods = nutrition?.food || []
	
	// Determine mode from meal_plan_type_id: 0 = custom/standard, 1 = simplified, 2 = logger
	const mealPlanTypeId = nutrition?.meal_plan_type_id ?? null
	const nutritionMode = mealPlanTypeId === 1 ? 'simplified' : mealPlanTypeId === 2 ? 'logger' : mealPlanTypeId === 0 ? 'custom' : null
console.log(mealPlanTypeId,nutritionMode)
	const macroTotalMap = nutritionItemstoMacroMap(foods)
	
	// State to track conversion in progress
	const [isConverting, setIsConverting] = useState(false)

	const customMacros = [].concat(
		...foods
			//get array of just the meals that are recipes
			.filter(item=>item.type === 'meal' && Boolean(item.recipe))
			.map(item=>item.recipe.macros)
	)

	const user = useSelector(state=>state.users.current_user)
	//get replace state. This holds the id and type of the exercise
	//getting replaced. id will be falsey
	const replace = useSelector(state=>state.resourceLibrary.replace)
	const replacing = Boolean(replace.id)

	const libraryIsOpen = useSelector(state=>state.resourceLibrary.show)

	const handleDragEnd = result => {
		const droppedOutsideOfList = !Boolean(result.destination)
		if (foods.length <= 1 || droppedOutsideOfList) return
		const {source:{index:srcIdx},destination:{index:tgtIdx}} = result
		//get a temp array with the src item removed
		const tmpNoSrc = [
			...foods.slice(0,srcIdx),
			...foods.slice(srcIdx+1)
		]
		//define the update with the src item at the target index
		//perform deep copy "hack" so we can mutate the update object
		//with the new foods orders
		const foodsUpdate = JSON.parse(JSON.stringify([
			...tmpNoSrc.slice(0,tgtIdx),
			foods[srcIdx],
			...tmpNoSrc.slice(tgtIdx)
		]))
		//update the foods orders to reflect the new order
		foodsUpdate.forEach((food,i)=>{
			food.routine_order = i+1
		})

		//dispatch async api action to persist the new order
		dispatch(updateNutritionOrder({
			nutrition_id,
			//map the update to array of typed ids
			new_order:foodsUpdate.map(item=>{
				switch(item.type){
					case "meal": return {id:item.id,type:"meal"}
					case "food": return {id:item.food_id,type:"food"}
					case "heading": return {id:item.id,type:"heading"}
				}
			}),
			//update is passed only so that we can set the new routine
			//in [updateRoutineOrder.pending] (ie optimistic UI update)
			foodsUpdate,
		}))
	}

	const handleSetMealPlanType = async (type) => {
		// Don't convert if already converting or if it's the same type
		if (isConverting || mealPlanTypeId === type) return
		
		setIsConverting(true)
		try {
			if (type === 1) {
				// Simplified
				await statusDispatch(makeNutritionSimplified({nutrition_id}), 'nutrition', 'convert')
			} else if (type === 0) {
				// Custom/Standard
				await statusDispatch(makeNutritionStandard({nutrition_id}), 'nutrition', 'convert')
			} else if (type === 2) {
				// Logger - use updateEvent to set meal_plan_type_id
				await statusDispatch(updateEvent({
					event_id: nutrition_id,
					event_type: 'nutrition',
					meal_plan_type_id: 2
				}), 'nutrition', 'convert')
			}
		} finally {
			setIsConverting(false)
		}
	}

	const handleAddHeading = ()=>{
		const routine_order = foods.length + 1
		dispatch(
			createNutritionHeading(
				{
					nutrition_id,
					routine_order
				}
			)
		)
	}

	console.log(nutrition)

	const subItem = (item,idx)=>{
		// Use simplified logger components when in logger mode
		if (nutritionMode === 'logger') {
			switch(item.type){
				case 'heading': return <Heading key={item.id} heading={item} idx={idx}/>
				case 'placeholder': return <Placeholder placeholder={item}/>
				case "meal": return <LoggerMeal key={item.id} meal={item} idx={idx}/>
				case "meal_photo": return <MealPhoto key={item.id} photo={item} idx={idx}/>
				default: return <LoggerFood key={item.food_id} food={item} idx={idx} />
			}
		}
		
		// Standard components for custom mode
		switch(item.type){
			case 'heading': return <Heading key={item.id} heading={item} idx={idx}/>
			case 'placeholder': return <Placeholder placeholder={item}/>
			case "meal": return <Meal key={item.id} meal={item} idx={idx}/>
			case "meal_photo": return <MealPhoto key={item.id} photo={item} idx={idx}/>
			default: return <Food key={item.food_id} food={item} idx={idx} />
		}
	}

	if(!foods) return (
		<div className="d-flex justify-content-center p-5">
			<Spinner animation="border" variant="primary"/>
		</div>
	)

	return (
		<div id="Foods" className="position-relative pl-3 pl-md-5">
			{/* Always show nutrition mode toggle at the top */}
			<div className="mb-3 d-flex justify-content-center">
				<ButtonGroup size="sm">
					<Button
						onClick={() => handleSetMealPlanType(0)}
						variant={mealPlanTypeId === 0 ? "primary" : "outline-primary"}
						disabled={isConverting}
					>
						Custom
					</Button>
					<Button
						onClick={() => handleSetMealPlanType(2)}
						variant={mealPlanTypeId === 2 ? "primary" : "outline-primary"}
						disabled={isConverting}
					>
						Logger
					</Button>
					<Button
						onClick={() => handleSetMealPlanType(1)}
						variant={mealPlanTypeId === 1 ? "primary" : "outline-primary"}
						disabled={isConverting}
					>
						Simplified
					</Button>
				</ButtonGroup>
			</div>
			{isConverting ? (
				<div className="d-flex justify-content-center align-items-center p-5">
					<Spinner animation="border" variant="primary" className="mr-2" />
					<span>Converting nutrition type...</span>
				</div>
			) : (
				<>
			{foods.length>0&&
				<div className="d-flex justify-content-end flex-wrap">
					{nutrition.simplified === 0 &&
						<small className={["mr-auto",replacing?'text-primary':'text-dark'].join(' ')}>
							<i className={["fas mr-1",replacing?'fa-exchange-alt':'fa-info-circle'].join(' ')}/>
							{	
								replacing ?
								'You are currently replacing a food item. Select from the library above'
								:
								'Click on a food item name to replace it with another'
							}
						</small>
					}
					<div className="ml-3 text-right">
						{nutrition.simplified === 0 &&
							<>
								{!Boolean(libraryIsOpen)&&
									<Button 
										onClick={()=>{
											dispatch(updateResourceLibrary({
												show:!libraryIsOpen,
												insertPosition:nutrition.food.length+1,
												tgtResourceType:'nutrition',
												resourceType:'recipe',
												tgtResourceId:nutrition_id,
												replace:{}
											}))
										}}
										variant={
											libraryIsOpen?
											"outline-danger":
											"outline-success"
										} 
										className="mr-1 mb-1" 
										size="sm"
									>
										Recipe/Meal <i className="fas fa-plus"/>
									</Button>
								}
								{/*<OverlayTrigger overlay={
									<Tooltip>{libraryIsOpen?"Close food library":"Add Foods"}</Tooltip>
								}>
									<Button 
										onClick={()=>{
											dispatch(updateResourceLibrary({
												show:!libraryIsOpen,
												insertPosition:false,
												tgtResourceType:'nutrition',
												resourceType:'food',
												tgtResourceId:nutrition_id
											}))
										}}
										variant={
											libraryIsOpen?
											"outline-danger":
											"outline-success"
										} 
										className="mr-1 mb-1" 
										size="sm"
									>
										{libraryIsOpen?'Library':'Foods'} <i className={libraryIsOpen?"fas fa-times":"fas fa-plus"}/>
									</Button>
								</OverlayTrigger>*/}
								
							</>
						}
					</div>
				</div>
			}
			<div>
				{
					(nutrition.simplified === 1 || mealPlanTypeId === 1) ? 
					<>
						<OverlayTrigger overlay={
						<Tooltip>
							Nutrition can be a number 1-20
						</Tooltip>
						}>
						<i className="ml-1 fas fa-info-circle" style={{marginTop:"5px", float:'right'}}/>
						</OverlayTrigger>
						<SimplifiedFoods 
							nutrition_id={nutrition_id}
							foods={foods}
						/> 
					</>
					:
					(
						//UX stuff: show a "get started" button if no foods
						//otherwise render the foods
						foods.length === 0 ?
						<div className="d-flex flex-column align-items-center py-5">
							{nutritionMode === 'custom' ? (
								<>
									{libraryIsOpen ? (
										<strong>Now select from the list above</strong>
									) : (
										<>
											<Button
												onClick={() => {
													dispatch(updateResourceLibrary({
														show:true,
														insertPosition:1,
														resourceType:'recipe_catalog',
														tgtResourceType:'nutrition',
														tgtResourceId:nutrition_id
													}))
												}}
												variant="outline-success" 
												size="lg"
												className="mb-2"
											>
												Recipe/Meal <i className="fas fa-plus"/>
											</Button>
											<Button
												onClick={() => {
													dispatch(updateResourceLibrary({
														show:true,
														insertPosition:1,
														resourceType:'food',
														tgtResourceType:'nutrition',
														tgtResourceId:nutrition_id
													}))
												}}
												variant="outline-success" 
												size="lg"
											>
												Food <i className="fas fa-plus"/>
											</Button>
										</>
									)}
								</>
							) : nutritionMode === 'logger' ? (
								<Button
									onClick={handleAddHeading}
									variant="outline-primary" 
									size="lg"
								>
									<i className="fas fa-heading mr-2"/> Add Heading
								</Button>
							) : null}
						</div>
						:
						<div> 
							{/* Foods */}
							<InsertButton
								nutrition_id={nutrition_id}
								insertPosition={1}
								tgtResourceType="nutrition"
							/>
							<DragDropContext onDragEnd={handleDragEnd}>
								<Droppable droppableId="nutrition-items" type="nutrition-item">
									{(provided, snapshot) => (
										<div
											{...provided.droppableProps}
											ref={provided.innerRef}
										>
											{foods.map(subItem)}
											{provided.placeholder}
										</div>
									)}
								</Droppable>
							</DragDropContext>
							{!user.isMAXUser&&
								<a href="https://www.fatsecret.com">
									<img src="https://platform.fatsecret.com/api/static/images/powered_by_fatsecret.png" srcset="https://platform.fatsecret.com/api/static/images/powered_by_fatsecret_2x.png 2x, https://platform.fatsecret.com/api/static/images/powered_by_fatsecret_3x.png 3x" border="0"/>
								</a>
							}
							<MacroTotals 
								macroTotalMap={macroTotalMap} 
								macroGoalsMap={{
									fat:nutrition.fat_goal,
									calories:nutrition.calorie_goal,
									carbs:nutrition.carb_goal,
									protein:nutrition.protein_goal,
									sodium:nutrition.sodium_goal,
									fiber:nutrition.fiber_goal,
								}} 
							/>
						</div>
					)
				}
			</div>
			</>
			)}
			<GroupMenu/>
		</div>
	)
}

export default Foods